import * as React from "react";
import TeamsBaseComponent, {ITeamsBaseComponentState} from "msteams-react-base-component";
import {DmsCaseInfo, FilingLocation, LinkType, PaginatedFilingLocations} from "../models";
import * as microsoftTeams from "@microsoft/teams-js";
import {IDropdownOption, MessageBar, MessageBarType, Pivot, PivotItem} from "office-ui-fabric-react";
import {TeamsDocumentStatusTabView} from "./TeamsDocumentStatusTabView";
import {DmsDocSearchTabView} from "./DmsDocSearchTabView";
import AuthenticateUser from "./AuthenticateUser";
import SharedFiles from "./SharedFiles";
import {TeamsApiService} from "../services/teams/TeamsApiService";
import {TeamsGetTeamsChannelsRequest} from "../services/teams/TeamsGetTeamsChannelsRequest";
import {ApiCallState} from "./ApiCallState";
import {AppInsightsLoggingForTeams, InitialiseAppInsights} from "./ApplicationInsights";
import {ApplicationInfo} from "./AppInfo";
import {TeamsGetTeamChannelsRequest} from "../services/teams/TeamsGetTeamChannelsRequest";
import {CustodianConfig} from "./components/CustodianConfig";
import {TeamsDocuments} from "./components/TeamsDocuments";
import {DmsDocuments} from "./components/DmsDocuments";
import {BrowseDocuments} from "./components/BrowseDmsDocuments";
import {SharedDocuments} from "./components/SharedDocuments";
import {initializeIcons} from "../../fabric-icons-subset/src/fabric-icons";

initializeIcons();

export interface ITeamsTabState extends ITeamsBaseComponentState {
    teamGroupId: string;
    userId: string;
    channelId: string;
    tenantId: string;
    showTeamsContent: boolean;
    channelName: string;
    caseInfo: DmsCaseInfo | null;
    storageType: string;
    teamsTheme: string;
    isTeamArchived: boolean;
    teamName: string;
    clientCollabLicensed: boolean;
    externalTeamChannels: PaginatedFilingLocations;
    externalTeamName: string;
    externalTeamId: string;
    tabViewKey: string;
    errorMessage: string;
    externalChannelsRetrievalState: ApiCallState;
    externalTeamNameRetrievalState: ApiCallState;
    teamChannels: IDropdownOption[];
    teamChannelsRetrievalState: ApiCallState;
    nonBlockingErrorMessage: string;
    showNonBlockingErrorMessage: boolean;
    isLitigationHoldEnabled: boolean;
    isFrictionlessSpExperienceEnabled: boolean;
    litigationHoldError: string;
    linkType: LinkType;
}

export interface ITeamsTabProps {}

export class TeamsTab extends TeamsBaseComponent<ITeamsTabProps, ITeamsTabState> {
    teamsApiService = new TeamsApiService();

    constructor(props: ITeamsTabProps) {
        super(props);
        this.state = {
            theme: null,
            teamsTheme: "default",
            teamGroupId: this.getQueryVariable("teamsId"),
            userId: this.getQueryVariable("userId"),
            channelId: this.getQueryVariable("channelId"),
            tenantId: this.getQueryVariable("tenantId"),
            showTeamsContent: false,
            channelName: this.getQueryVariable("channelName"),
            caseInfo: null,
            storageType: "",
            isTeamArchived: false,
            teamName: "",
            clientCollabLicensed: null,
            externalTeamChannels: null,
            externalTeamName: null,
            externalTeamId: null,
            tabViewKey: "teamsfilestab",
            errorMessage: null,
            externalTeamNameRetrievalState: ApiCallState.Loading,
            externalChannelsRetrievalState: ApiCallState.Loading,
            teamChannels: [],
            teamChannelsRetrievalState: ApiCallState.Loading,
            nonBlockingErrorMessage: null,
            showNonBlockingErrorMessage: false,
            isLitigationHoldEnabled: false,
            isFrictionlessSpExperienceEnabled: false,
            litigationHoldError: "",
            linkType: LinkType.Workspace
        };

        const appInsights = InitialiseAppInsights(ApplicationInfo);
        AppInsightsLoggingForTeams(
            ApplicationInfo,
            appInsights,
            this.state.tenantId,
            this.state.teamGroupId,
            this.state.userId
        );
    }

    public async componentWillMount(): Promise<void> {
        microsoftTeams.initialize(() => {
            microsoftTeams.getContext(
                function (context: {theme: string; teamName: string}) {
                    this.setState({teamsTheme: context.theme, teamName: context.teamName});
                }.bind(this)
            );
        });
    }

    public async componentDidMount() {
        microsoftTeams.registerOnThemeChangeHandler(
            function (theme: string) {
                this.setState({teamsTheme: theme});
            }.bind(this)
        );

        const tabViewKey = window.localStorage.getItem("ContentConnectorViewKey");
        if (tabViewKey) {
            this.setState({tabViewKey: tabViewKey});
        }
    }

    componentDidUpdate(prevProps: Readonly<ITeamsTabProps>, prevState: Readonly<ITeamsTabState>): void {
        if (this.state.externalTeamId && !prevState.externalTeamId) {
            this.getExternalTeamInfo();
        }
    }

    async getExternalTeamInfo() {
        const getChannelsRequest: TeamsGetTeamsChannelsRequest = {
            channelId: this.state.channelId,
            externalTeamId: this.state.externalTeamId,
            recentDocs: false,
            teamId: this.state.teamGroupId,
            tenantId: this.state.tenantId,
            userId: this.state.userId,
        };

        this.setState({
            externalTeamNameRetrievalState: ApiCallState.Loading,
            externalChannelsRetrievalState: ApiCallState.Loading,
        });

        await this.teamsApiService.getTeamsChannels(
            getChannelsRequest,
            "External Teams channels retrieval failed.",
            (teamChannels) => {
                this.setState({
                    externalTeamChannels: {Page: teamChannels as FilingLocation[], PagingToken: null, Errors: null},
                    externalChannelsRetrievalState: ApiCallState.Success,
                });
                this.setState((prevState) => {
                    return {clientCollabLicensed: prevState.clientCollabLicensed != false ? true : false};
                });
            },
            (errorMessage, errorStatusCode) => {
                if (errorStatusCode == "403") {
                    this.setState({clientCollabLicensed: false});
                } else {
                    this.setState({
                        nonBlockingErrorMessage: errorMessage,
                        externalChannelsRetrievalState: ApiCallState.Error,
                        clientCollabLicensed: false,
                        showNonBlockingErrorMessage: true,
                    });
                }
            }
        );

        await this.teamsApiService.getExternalTeamName(
            {externalTeamId: this.state.externalTeamId, teamId: this.state.teamGroupId, tenantId: this.state.tenantId},
            "External Team name retrieval failed.",
            (externalTeamName) => {
                this.setState({
                    externalTeamName: externalTeamName,
                    externalTeamNameRetrievalState: ApiCallState.Success,
                });

                this.setState((prevState) => {
                    return {clientCollabLicensed: prevState.clientCollabLicensed != false ? true : false};
                });
            },
            (errorMessage, errorStatusCode) => {
                if (errorStatusCode == "403") {
                    // Don't set an error message in the UI.
                    // Disable sharing functionality if the user doesn't have access to the external Team.
                    this.setState({clientCollabLicensed: false});
                } else {
                    this.setState({
                        nonBlockingErrorMessage: errorMessage,
                        externalTeamNameRetrievalState: ApiCallState.Error,
                        clientCollabLicensed: false,
                        showNonBlockingErrorMessage: true,
                    });
                }
            }
        );
    }

    async isTeamArchived() {
        await this.teamsApiService.getTeamArchiveStatus(
            {teamId: this.state.teamGroupId, tenantId: this.state.tenantId},
            "Team archive status retrieval failed.",
            (isArchived) => this.setState({isTeamArchived: isArchived}),
            (errorMessage) => this.setState({errorMessage: errorMessage})
        );
    }

    async loadChannels(caseInfo: DmsCaseInfo) {
        const linkedLocation = JSON.parse(caseInfo.CaseDetails);

        const request: TeamsGetTeamChannelsRequest = {
            teamId: this.state.teamGroupId,
            channelId: this.state.channelId,
            tenantId: this.state.tenantId,
            spHostKVPrefix: linkedLocation.SpHostKVPrefix,
        };

        await this.teamsApiService.getTeamChannels(
            request,
            "Team channels retrieval failed.",
            (response) => {
                const dropdownOptions = response as IDropdownOption[];
                this.setState({
                    teamChannels: dropdownOptions,
                    teamChannelsRetrievalState: ApiCallState.Success,
                    channelId: dropdownOptions.filter((i) => i.selected)[0].key.toString(),
                });
            },
            (error) => this.setState({errorMessage: error, teamChannelsRetrievalState: ApiCallState.Error})
        );
    }

    setPivotKey(item: PivotItem) {
        this.setState({tabViewKey: item.props.itemKey});
    }

    public render() {
        if (this.state.errorMessage) {
            return (
                <div>
                    <p style={{color: "red"}}>{this.state.errorMessage}</p>
                </div>
            );
        }

        if (!this.state.caseInfo) {
            return (
                <AuthenticateUser
                    tenantId={this.state.tenantId}
                    userId={this.state.userId}
                    teamGroupId={this.state.teamGroupId}
                    channelId={this.state.channelId}
                    AuthenticatedWithCase={this.isAuthenticatedWithCase.bind(this)}
                />
            );
        }

        const isDarkMode = this.state.teamsTheme !== "default";

        return (
            <div style={{height: "100%"}}>
                {this.state.showNonBlockingErrorMessage && (
                    <div>
                        <hr style={{height: "10px", visibility: "hidden"}} />
                        <MessageBar
                            messageBarType={MessageBarType.error}
                            isMultiline={true}
                            onDismiss={() => this.setState({showNonBlockingErrorMessage: false})}
                        >
                            <div style={{fontSize: "14px"}}>{this.state.nonBlockingErrorMessage}</div>
                        </MessageBar>
                        <hr style={{height: "10px", visibility: "hidden"}} />
                    </div>
                )}
                <TeamsDocuments caseInfo={this.state.caseInfo}
                                setLitigationHoldValue={(val) => this.setState({isLitigationHoldEnabled: val})}
                                setLitigationHoldError={(val) => this.setState({litigationHoldError: val})}
                                setFrictionlessExperienceValue={(val) => this.setState({isFrictionlessSpExperienceEnabled: val})}>
                    <DmsDocuments caseInfo={this.state.caseInfo}
                                  linkType={this.state.linkType}>
                        <BrowseDocuments>
                            <SharedDocuments clientCollabLicensed={this.state.clientCollabLicensed}>
                                <CustodianConfig blockRender={true}>
                                    <Pivot
                                        onLinkClick={(item) => this.setPivotKey(item)}
                                        selectedKey={this.state.tabViewKey}
                                        className={isDarkMode ? "darkMode" : ""}
                                    >
                                        <PivotItem itemKey="teamsfilestab" headerText="Team Files">
                                            <div
                                                id="container"
                                                style={{paddingTop: "10px", height: "calc(100% - 5px)"}}
                                            >
                                                <TeamsDocumentStatusTabView
                                                    CaseInfo={this.state.caseInfo}
                                                    darkMode={isDarkMode}
                                                    teamName={this.state.teamName}
                                                    teamChannels={this.state.teamChannels}
                                                    channelId={this.state.channelId}
                                                    teamChannelsRetrievalState={this.state.teamChannelsRetrievalState}
                                                    externalTeamChannels={this.state.externalTeamChannels}
                                                    externalTeamName={this.state.externalTeamName}
                                                    clientCollabLicensed={this.state.clientCollabLicensed}
                                                    externalChannelsRetrievalState={
                                                        this.state.externalChannelsRetrievalState
                                                    }
                                                    externalTeamNameRetrievalState={
                                                        this.state.externalTeamNameRetrievalState
                                                    }
                                                    isLitigationHoldEnabled={this.state.isLitigationHoldEnabled}
                                                    isFrictionlessSpExperienceEnabled={this.state.isFrictionlessSpExperienceEnabled}
                                                    litigationHoldError={this.state.litigationHoldError}
                                                />
                                            </div>
                                        </PivotItem>
                                        <PivotItem
                                            itemKey="dmsfilestab"
                                            headerText={TeamsTab.getStorageTypeLabel(this.state.storageType)}
                                        >
                                            <div
                                                id="container"
                                                style={{paddingTop: "10px", height: "calc(100% - 5px)"}}
                                            >
                                                <DmsDocSearchTabView
                                                    caseInfo={this.state.caseInfo}
                                                    darkMode={isDarkMode}
                                                    IsTeamArchived={this.state.isTeamArchived}
                                                    TeamName={this.state.teamName}
                                                    externalTeamChannels={this.state.externalTeamChannels}
                                                    ExternalTeamName={this.state.externalTeamName}
                                                    clientCollabLicensed={this.state.clientCollabLicensed}
                                                    externalTeamNameRetrievalState={
                                                        this.state.externalTeamNameRetrievalState
                                                    }
                                                    externalChannelsRetrievalState={
                                                        this.state.externalChannelsRetrievalState
                                                    }
                                                    isLitigationHoldEnabled={this.state.isLitigationHoldEnabled}
                                                    isFrictionlessSpExperienceEnabled={this.state.isFrictionlessSpExperienceEnabled}
                                                    linkType={this.state.linkType}
                                                />
                                            </div>
                                        </PivotItem>
                                        {this.state.clientCollabLicensed && 
                                        !this.state.isLitigationHoldEnabled &&
                                        (
                                            <PivotItem itemKey="sharedfilestab" headerText="Shared">
                                                <div
                                                    id="container"
                                                    style={{paddingTop: "10px", height: "calc(100% - 5px)"}}
                                                >
                                                    <SharedFiles
                                                        TeamsId={this.state.teamGroupId}
                                                        StorageType={this.state.caseInfo.StorageType}
                                                        DarkMode={isDarkMode}
                                                        ExternalTeamName={this.state.externalTeamName}
                                                        CaseInfo={this.state.caseInfo}
                                                        TeamName={this.state.teamName}
                                                        isLitigationHoldEnabled={this.state.isLitigationHoldEnabled}
                                                    />
                                                </div>
                                            </PivotItem>
                                        )}
                                    </Pivot>
                                </CustodianConfig>
                            </SharedDocuments>
                        </BrowseDocuments>
                    </DmsDocuments>
                </TeamsDocuments>
            </div>
        );
    }

    public static getStorageTypeLabel(storageType: string) {
        if (storageType === null) {
            return "none";
        }

        switch (storageType) {
            case "imanage":
                return "iManage™";
            case "netdocuments":
                return "NetDocuments";
            case "sharepoint":
                return "SharePoint";
        }

        return storageType;
    }

    private isAuthenticatedWithCase(storageType: string, caseInfo: DmsCaseInfo, externalTeamId: string) {

        const linkedLocation = JSON.parse(caseInfo.LinkedMatterUrl);
        let linkType = LinkType.Workspace
        if (linkedLocation.LinkedFolderId != null) {
            linkType = LinkType.Folder;
        }

        this.setState({storageType, caseInfo, externalTeamId, linkType});
        this.isTeamArchived();
        this.loadChannels(caseInfo);
    }
}
